import { useCallback, useReducer, useRef } from 'react';
import { CircuitSchema } from '../types';

const MAX_HISTORY = 50;

export interface UseHistoryReturn {
  pushState: (schema: CircuitSchema) => void;
  /** Fold an automatic follow-up edit into the current step instead of adding one. */
  replaceState: (schema: CircuitSchema) => void;
  undo: () => CircuitSchema | null;
  redo: () => CircuitSchema | null;
  canUndo: boolean;
  canRedo: boolean;
}

interface HistoryEntry {
  /** Full snapshot, restored verbatim on undo/redo. */
  snapshot: string;
  /** Comparison key ignoring fields the canvas writes back on its own. */
  key: string;
}

/**
 * Fields React Flow maintains itself: it measures `width`/`height` a frame
 * after a node mounts and writes them back, and it flips `selected`/`dragging`
 * during ordinary interaction. Those echoes are not user edits, so they must
 * not become undo steps — otherwise restoring a state makes the canvas echo a
 * slightly different one, which gets recorded as a *new* state and clears the
 * redo stack, leaving undo cycling without ever moving.
 */
const VOLATILE_KEYS = [
  'width',
  'height',
  'selected',
  'dragging',
  'resizing',
  'positionAbsolute',
  'zIndex',
];

function stripVolatile(item: any): any {
  if (!item || typeof item !== 'object') return item;
  const copy: Record<string, any> = {};
  for (const [key, value] of Object.entries(item)) {
    if (VOLATILE_KEYS.includes(key)) continue;
    copy[key] = value;
  }
  return copy;
}

function toEntry(schema: CircuitSchema): HistoryEntry {
  const nodes = schema.nodes || [];
  const edges = schema.edges || [];
  const annotations = schema.annotations || [];

  return {
    snapshot: JSON.stringify({ nodes, edges, annotations }),
    key: JSON.stringify({
      nodes: nodes.map(stripVolatile),
      edges: edges.map(stripVolatile),
      annotations: annotations.map(stripVolatile),
    }),
  };
}

export function useHistory(): UseHistoryReturn {
  // Refs rather than state: undo/redo must act on the current stack even when
  // presses arrive faster than React can re-render, which a state-closured
  // implementation cannot do.
  const past = useRef<HistoryEntry[]>([]);
  const future = useRef<HistoryEntry[]>([]);
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  const pushState = useCallback((schema: CircuitSchema) => {
    const entry = toEntry(schema);
    const current = past.current[past.current.length - 1];

    // Nothing meaningful changed (viewport pan, selection, a measurement echo).
    if (current && current.key === entry.key) return;

    past.current.push(entry);
    if (past.current.length > MAX_HISTORY) past.current.shift();
    future.current = [];
    forceRender();
  }, []);

  const replaceState = useCallback((schema: CircuitSchema) => {
    const entry = toEntry(schema);
    if (past.current.length === 0) past.current.push(entry);
    else past.current[past.current.length - 1] = entry;
    forceRender();
  }, []);

  const undo = useCallback((): CircuitSchema | null => {
    // The first entry is the state the session started from; there is nothing
    // before it to step back to.
    if (past.current.length < 2) return null;

    future.current.unshift(past.current.pop()!);
    const previous = past.current[past.current.length - 1];
    forceRender();
    return JSON.parse(previous.snapshot);
  }, []);

  const redo = useCallback((): CircuitSchema | null => {
    if (future.current.length === 0) return null;

    const next = future.current.shift()!;
    past.current.push(next);
    forceRender();
    return JSON.parse(next.snapshot);
  }, []);

  return {
    pushState,
    replaceState,
    undo,
    redo,
    canUndo: past.current.length > 1,
    canRedo: future.current.length > 0,
  };
}
